import React, { createContext, useContext } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import MeetingNoteFlow from './MeetingNoteFlow';

type MeetingNoteContextType = {
  opened: boolean;
  open: () => void;
  close: () => void;
};

const MeetingNoteContext = createContext<MeetingNoteContextType>({} as MeetingNoteContextType);

export function useMeetingNote() {
  const context = useContext(MeetingNoteContext);
  if (context === undefined) {
    throw new Error('useMeetingNote must be used within a MeetingNoteProvider');
  }
  return context;
}

export default function MeetingNoteProvider({ children }: { children: React.ReactNode }) {
  const [opened, { open: disclosureOpen, close }] = useDisclosure(false);
  const { flagEnabledNewLayout } = useFlags();
  const { client } = useCurrentClientIfAvailable();
  const navigate = useNavigate();

  const value = {
    opened,
    open: () => {
      if (flagEnabledNewLayout) {
        navigate(`/client/${client?.id}/meeting-note`, {
          state: { fromPath: location.pathname },
        });
        return;
      }
      disclosureOpen();
    },
    close,
  };

  return (
    <MeetingNoteContext value={value}>
      {children}
      <MeetingNoteFlow opened={opened} close={close} />
    </MeetingNoteContext>
  );
}
